import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAiAnswer } from '../services/geminiService';
import { useCountry } from '../context/CountryContext';
import LoadingSpinner from '../components/LoadingSpinner';
import MarkdownRenderer from '../components/MarkdownRenderer';
import ShareButton from '../components/ShareButton';
import { AiSearchResult } from '../types';

const AiResponsePage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    const [currentQuery, setCurrentQuery] = useState(searchParams.get('q') || '');
    const [searchInputValue, setSearchInputValue] = useState(searchParams.get('q') || '');
    const [response, setResponse] = useState<AiSearchResult | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    
    const { country, availableCountries } = useCountry();
    const countryName = availableCountries.find(c => c.code === country)?.name || 'your region';

    const fetchAnswer = async (queryToFetch: string) => {
        if (!queryToFetch.trim()) return;
        setIsLoading(true);
        setResponse(null); // Clear previous response

        try {
            const result = await getAiAnswer(queryToFetch, countryName);
            setResponse(result);
        } catch (error) {
            console.error("Error fetching AI answer:", error);
            setResponse({ text: "Sorry, I encountered an error while trying to answer your question. Please try again.", sources: [] });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const queryFromUrl = searchParams.get('q') || '';
        setCurrentQuery(queryFromUrl);
        setSearchInputValue(queryFromUrl);
        if (queryFromUrl) {
            fetchAnswer(queryFromUrl);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, countryName]);

    const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const newQuery = searchInputValue.trim();
        if (!newQuery) return;

        const words = newQuery.split(' ');
        const isQuestion = words.length > 3 || newQuery.endsWith('?') || ['what', 'how', 'why', 'explain'].includes(words[0].toLowerCase());

        if (isQuestion) {
            setSearchParams({ q: newQuery });
        } else {
            navigate(`/ai-decoder?code=${encodeURIComponent(newQuery)}`);
        }
    };

    return (
        <div className="bg-gray-50 min-h-full">
            <div className="container mx-auto px-4 py-12 max-w-screen-md">
                <header className="text-center mb-10">
                    <h1 className="text-4xl font-extrabold font-poppins tracking-tight text-gray-900 sm:text-5xl">AI Financial Assistant</h1>
                    <p className="mt-4 text-xl text-gray-600">Your question, answered by AI.</p>
                </header>

                <section className="bg-white p-8 rounded-xl shadow-lg border border-gray-200 mb-8">
                    <form onSubmit={handleSearch}>
                        <label htmlFor="ai-question" className="block text-sm font-medium text-gray-700 mb-2">Ask another question or search a code</label>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                id="ai-question"
                                type="search"
                                value={searchInputValue}
                                onChange={(e) => setSearchInputValue(e.target.value)}
                                className="flex-grow w-full p-3 text-base text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-[#246BFD] focus:border-[#246BFD]"
                                placeholder="Search a charge or ask a question..."
                                required
                            />
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="text-white bg-[#246BFD] hover:bg-opacity-90 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-base px-6 py-3 disabled:bg-blue-300"
                            >
                                {isLoading ? 'Thinking...' : 'Ask'}
                            </button>
                        </div>
                    </form>
                </section>

                <section>
                    {isLoading && !response && <LoadingSpinner />}
                    {response && (
                        <div className="space-y-6">
                            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                                <h2 className="text-lg font-semibold font-poppins text-blue-800">Your Question:</h2>
                                <p className="text-blue-700 mt-2 italic">"{currentQuery}"</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                                 <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold font-poppins text-gray-800">AI's Answer:</h2>
                                    <ShareButton
                                        title={`AI Answer for "${currentQuery}" | ChargesDecoder`}
                                        text={`I asked ChargesDecoder's AI about "${currentQuery}" and here's the answer!`}
                                    />
                                </div>
                                <MarkdownRenderer content={response.text} />
                                {response.sources && response.sources.length > 0 && (
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <h3 className="text-sm font-semibold text-gray-700 mb-2">Sources:</h3>
                                        <ul className="space-y-2">
                                            {response.sources.map((source, index) => source.web && (
                                                <li key={index} className="flex items-start text-sm">
                                                    <span className="text-gray-500 mr-2 flex-shrink-0">[{index + 1}]</span>
                                                    <a
                                                        href={source.web.uri}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:underline break-all"
                                                        title={source.web.uri}
                                                    >
                                                        {source.web.title}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {!isLoading && !response && currentQuery && (
                         <div className="text-center py-10 px-6 bg-white rounded-lg shadow-sm border border-gray-200">
                            <h2 className="text-xl font-medium text-gray-800">No Answer Received</h2>
                            <p className="text-gray-500 mt-2">There might be an issue with the AI service. Please try asking your question again.</p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default AiResponsePage;
